import { getBrowserSupabaseClient } from "@wtc/supabase";
import type { Database } from "@/lib/supabase/database.types";
import { handleSupabaseError } from "./error";

type PublicTables = Database["public"]["Tables"];
/** Any table in the public schema, as the generated types know it. */
export type PublicTableName = keyof PublicTables;
/** The insert payload shape for a given table, codegen-derived. */
export type PublicTableInsert<T extends PublicTableName> = PublicTables[T]["Insert"];
/**
 * What a caller of the helpers below actually supplies: the table's insert
 * payload with `user_id` optional, because these helpers are the thing that
 * fills it in from the session.
 */
export type PublicTableInsertWithoutUserId<T extends PublicTableName> = Omit<
  PublicTableInsert<T>,
  "user_id"
> & { user_id?: string | null };

// ============================================================================
// AUTO USER ID UTILITIES
// ============================================================================

/**
 * Automatically adds user_id to data if the table requires it
 * This prevents "null value in column user_id violates not-null constraint" errors
 */
export async function addUserIdToData<T extends PublicTableName>(
  data: PublicTableInsertWithoutUserId<T>,
  tableName: T
): Promise<PublicTableInsert<T>> {
  const supabase = getBrowserSupabaseClient();

  // Get the current user
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError || !user) {
    throw new Error("User not authenticated");
  }

  // Check if the table requires user_id by looking at the data structure
  // Tables that typically require user_id: time_entries, tickets, contact_notes, etc.
  const tablesRequiringUserId: readonly PublicTableName[] = [
    "time_entries",
    // tickets do not have user_id; they use created_by
    "contact_notes",
    "task_notes",
    "contact_tasks",
    "contact_meetings",
    "contact_proposals",
    "ticket_updates",
    "ticket_attachments",
  ];

  // If the table requires user_id and it's not already set, add it
  const alreadyHasUserId = "user_id" in data && Boolean(data.user_id);
  if (tablesRequiringUserId.includes(tableName) && !alreadyHasUserId) {
    return {
      ...data,
      user_id: user.id,
    };
  }

  return data;
}

/**
 * Enhanced Supabase insert with automatic user_id addition
 */
export async function insertWithUserId<T extends PublicTableName>(
  tableName: T,
  data: PublicTableInsertWithoutUserId<T>
) {
  const supabase = getBrowserSupabaseClient();
  const dataWithUserId = await addUserIdToData(data, tableName);

  return supabase.from(tableName).insert([dataWithUserId]).select().single();
}

/**
 * Enhanced Supabase insertMany with automatic user_id addition
 */
export async function insertManyWithUserId<T extends PublicTableName>(
  tableName: T,
  dataArray: PublicTableInsertWithoutUserId<T>[]
) {
  const supabase = getBrowserSupabaseClient();

  // Add user_id to all items in the array
  const dataWithUserId = await Promise.all(
    dataArray.map((data) => addUserIdToData(data, tableName))
  );

  return supabase.from(tableName).insert(dataWithUserId).select();
}

/**
 * Enhanced insertWithUserId with standardized error handling
 */
export async function insertWithUserIdSafe<T extends PublicTableName>(
  tableName: T,
  data: PublicTableInsertWithoutUserId<T>
) {
  const { data: result, error } = await insertWithUserId(tableName, data);

  if (error) {
    handleSupabaseError(error, `create ${tableName}`);
  }

  return result;
}
